import { environment } from '@base/environments/environment';
import { Constants } from '@base/src/app/models/constants';
import {
  cleanLocaleNumberInput,
  getDecimalSeparator,
} from '@base/src/app/pages/accounts/utils/locale-number-input.util';

/**
 * Tenant-aware decimal amounts: calculations keep the full value,
 * only the displayed text is truncated to `maxDecimals`.
 */

export type DecimalAmountInput = string | number | null | undefined;

export const DECIMAL_AMOUNT_MAX_DECIMALS = 2;

/** Extra digits used before cutting, so float noise (8.2 * 100 = 819.99…) does not drop a unit. */
const TRUNCATION_GUARD_DIGITS = 6;

function getNode(): string {
  return environment.node ?? environment.tenant_id ?? Constants.TENANT_ID_AR;
}

export function truncateDecimalAmount(
  value: number,
  maxDecimals: number = DECIMAL_AMOUNT_MAX_DECIMALS
): number {
  if (!Number.isFinite(value)) return 0;

  const [INTEGER_PART, DECIMAL_PART = ''] = value
    .toFixed(maxDecimals + TRUNCATION_GUARD_DIGITS)
    .split('.');
  const TRUNCATED = Number(
    `${INTEGER_PART}.${DECIMAL_PART.slice(0, maxDecimals)}`
  );

  return TRUNCATED === 0 ? 0 : TRUNCATED;
}

/** Input text (tenant separator) → full numeric value. */
export function parseDecimalAmount(value: DecimalAmountInput): number {
  return cleanLocaleNumberInput(value, getNode());
}

/** Full numeric value → input text, truncated and with the tenant separator (no thousands). */
export function formatDecimalAmount(
  value: number,
  maxDecimals: number = DECIMAL_AMOUNT_MAX_DECIMALS
): string {
  if (!Number.isFinite(value)) return '0';

  const FORMATTED = truncateDecimalAmount(value, maxDecimals).toFixed(
    maxDecimals
  );
  const [INTEGER_PART, DECIMAL_PART = ''] = FORMATTED.split('.');

  if (!DECIMAL_PART || /^0+$/.test(DECIMAL_PART)) {
    return INTEGER_PART;
  }

  return `${INTEGER_PART}${getDecimalSeparator(getNode())}${DECIMAL_PART}`;
}

/**
 * Editable amount bound to a `qr-input`:
 * - `value`: full value for calculations and payloads.
 * - `display`: text bound to `[value]`; only updated on `set`/`commit`, never while typing.
 */
export class DecimalAmountField {
  value = 0;
  display = '';
  private draft?: string;

  constructor(
    initialValue: DecimalAmountInput = 0,
    private readonly maxDecimals: number = DECIMAL_AMOUNT_MAX_DECIMALS
  ) {
    this.set(parseDecimalAmount(initialValue));
  }

  get hasDraft(): boolean {
    return this.draft !== undefined;
  }

  /** Sets the value from code (API load, distribution, totals) and discards any draft. */
  set(value: number): void {
    this.value = Number.isFinite(value) ? value : 0;
    this.display = formatDecimalAmount(this.value, this.maxDecimals);
    this.draft = undefined;
  }

  /**
   * Bind to `(valueChange)`: stores the typed text without parsing or reformatting.
   * `qr-input` also emits when `[value]` changes from code; that echo matches
   * `display` and must not become a draft (it would replace `value` with the truncated text).
   */
  onInput(rawValue: DecimalAmountInput): void {
    const TEXT =
      rawValue === null || rawValue === undefined ? '' : String(rawValue);
    this.draft = TEXT === this.display ? undefined : TEXT;
  }

  /** Bind to `(blurChange)`: parses the draft. Returns true when `value` changed. */
  commit(): boolean {
    if (this.draft === undefined) return false;

    const PREVIOUS_VALUE = this.value;
    this.set(parseDecimalAmount(this.draft));

    return this.value !== PREVIOUS_VALUE;
  }
}
